import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS, U3148ROLES } from "../../../../U3148";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { emptyOrganizationPersonalization, isU3148GoogleCalendarPersonalization, SysOrgPersonalizationNameType, U3148_TIME_SESSION_ROUNDING_OPTIONS, u3148IsValidTimeSessionRoundingMinutes, type U3148OrganizationPersonalization } from "../../../../U3148Service";
import type { U3148OrganizationFormExtendedHandlers } from "../../../U3148OrganizationForm";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u3148OrganizationPersonalizationHandler } from "./U3148OrganizationPersonalizationHandlers";
import U3148OrganizationPersonalizationEditDlg from "./U3148OrganizationPersonalizationEditDlg";
import U3148OrganizationPersonalizationDeleteDlg from "./U3148OrganizationPersonalizationDeleteDlg";
import { ORGANIZATION_PERSONALIZATION_NAME_TYPES } from "../../../common/organizationPersonalizationTypes";

function isValidIanaTimezone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

// Typage des props du composant
interface U3148OrganizationPersonalizationBodyProps {
  u3148OrganizationFormHandlers: U3148OrganizationFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des personnalisations d'une organisation
function getColumnsDef(t: TFunction, i18n: any): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
      },
      display: (column: any, value: any, row: any, allowRowSelection: boolean, rowUpdated?: (row: any) => void) => {
        return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allowRowSelection} />;
      },
      isCriteriaField: false,
    },
    {
      name: "personalizationName",
      label: t("lblPersonalizationName", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[350px]",
      type: "string",
      align: "left",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      display: (_column: any, value: any, _row: any) => {
        const personalizationType = ORGANIZATION_PERSONALIZATION_NAME_TYPES.find((type) => type.value === value);
        if (personalizationType) {
          const label = typeof personalizationType.label === "object" ? (i18n.language === "fr" ? personalizationType.label.fr : personalizationType.label.en) : String(personalizationType.label);
          return <span>{label}</span>;
        }
        return <span>{value}</span>;
      },
    },
    {
      name: "scope",
      label: t("lblScope", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[100px]",
      type: "string",
      align: "left",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      display: (_column: any, value: "admin" | "user" | undefined) => {
        if (!value) return null;
        const label = value === "admin" ? t("lblScopeAdmin", { ns: NS }) : t("lblScopeUser", { ns: NS });
        const badgeClass = value === "admin" ? "bg-slate-200 text-slate-800" : "bg-blue-100 text-blue-800";
        return <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${badgeClass}`}>{label}</span>;
      },
    },
    {
      name: "personalizationValue",
      label: t("lblPersonalizationValue", { ns: NS }),
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      display: (_column: any, value: any, row: U3148OrganizationPersonalization) => {
        if (row.personalizationName === SysOrgPersonalizationNameType.ORG_TIME_SESSION_ROUNDING_MINUTES) {
          const n = Number(value);
          if (U3148_TIME_SESSION_ROUNDING_OPTIONS.includes(n as (typeof U3148_TIME_SESSION_ROUNDING_OPTIONS)[number])) {
            return <span>{t("lblTimeSessionRoundingMinutes", { ns: NS, count: n })}</span>;
          }
        }
        return <span>{value}</span>;
      },
    },
  ];
}

// Composant principal pour la gestion des personnalisations d'une organisation
const U3148OrganizationPersonalizationBody: React.FC<U3148OrganizationPersonalizationBodyProps> = ({ u3148OrganizationFormHandlers: u3148OrganizationFormSHandlers }) => {
  // Translation
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<U3148OrganizationPersonalization>();

  const validateOrganizationPersonalization = (organizationPersonalization: U3148OrganizationPersonalization) => {
    const result: Record<string, string> = {};
    const persoName = organizationPersonalization.personalizationName;
    const persoValue = organizationPersonalization.personalizationValue?.trim() ?? "";

    if (!persoName) result.personalizationName = t("errPersonalizationNameRequired", { ns: NS });
    if (!persoValue) {
      result.personalizationValue = t("errPersonalizationValueRequired", { ns: NS });
    } else if (persoName === SysOrgPersonalizationNameType.ORG_PROJECT_INACTIVITY_DAYS) {
      const n = Number(persoValue);
      if (!Number.isInteger(n) || n < 1 || n > 999) {
        result.personalizationValue = t("errOrgProjectInactivityDaysInvalid", { ns: NS });
      }
    } else if (persoName === SysOrgPersonalizationNameType.ORG_TIMEZONE) {
      if (!isValidIanaTimezone(persoValue)) {
        result.personalizationValue = t("errOrgTimezoneInvalid", { ns: NS });
      }
    } else if (persoName === SysOrgPersonalizationNameType.ORG_TIME_SESSION_ROUNDING_MINUTES) {
      if (!u3148IsValidTimeSessionRoundingMinutes(persoValue)) {
        result.personalizationValue = t("errOrgTimeSessionRoundingInvalid", { ns: NS });
      }
    }

    // no duplicate Organization Personalizations (by personalizationName)
    const duplicate = gridStateHandlers
      .getData()
      .find((item) => item.personalizationName === organizationPersonalization.personalizationName && item.id !== organizationPersonalization.id);
    if (duplicate) {
      result.personalizationName = t("errOrganizationPersonalizationDuplicate", { ns: NS });
    }

    return result;
  };

  const u3148OrganizationPersonalizationBL: U2000FormSBusinessHandlers<U3148OrganizationPersonalization> = {
    newRecord: () => {
      return emptyOrganizationPersonalization;
    },

    // validation du formulaire
    validateForm: validateOrganizationPersonalization,
  };

  const u3148OrganizationPersonalizationformStateHandlers = useU2000FormStateHandlers<U3148OrganizationPersonalization>({
    formState,
    formStateDispatch,
    businessHandlers: u3148OrganizationPersonalizationBL,
  });

  const u3148OrganizationPersonalizationHandlers = u3148OrganizationPersonalizationHandler({ gridStateHandlers, t, formStateHandlers: u3148OrganizationPersonalizationformStateHandlers });

  // Transposer les données du formStateHandlers au gridStateHandlers pour OrganizationPersonalizations
  useEffect(() => {
    const columns = getColumnsDef(t, i18n);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    const organizationPersonalizations = u3148OrganizationFormSHandlers.getData?.()?.personalizations;
    const filteredPersonalizations = (Array.isArray(organizationPersonalizations) ? organizationPersonalizations : []).filter(
      (p) => !isU3148GoogleCalendarPersonalization(p.personalizationName) && p.scope !== "admin",
    );
    gridStateHandlers.setData(filteredPersonalizations);
    gridStateHandlers.setTotalRows(filteredPersonalizations.length);
  }, [u3148OrganizationFormSHandlers, t, i18n]);

  useEffect(() => {
    if (u3148OrganizationPersonalizationHandlers.dataChangedEvent === 0) return;
    const gridPersonalizations = gridStateHandlers.getData();
    const org = u3148OrganizationFormSHandlers.getData?.();
    const googleCalendarPersos = (org?.personalizations ?? []).filter((p) =>
      isU3148GoogleCalendarPersonalization(p.personalizationName),
    );
    u3148OrganizationFormSHandlers.setData({
      ...org,
      personalizations: [...gridPersonalizations, ...googleCalendarPersos],
    });
  }, [u3148OrganizationPersonalizationHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u3148OrganizationPersonalizationHandlers.onAdd,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3148OrganizationPersonalizationHandlers.onModify,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u3148OrganizationPersonalizationHandlers.onDelete,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3148OrganizationPersonalizationHandlers]
  );

  // Render
  return (
    <>
      <U3148OrganizationPersonalizationEditDlg u3148OrganizationPersonalizationHandler={u3148OrganizationPersonalizationHandlers} t={t} NS={NS} />
      <U3148OrganizationPersonalizationDeleteDlg u3148OrganizationPersonalizationHandler={u3148OrganizationPersonalizationHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblOrganizationPersonalization", {
          count: u3148OrganizationPersonalizationHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3148OrganizationPersonalizationBody;
